/**
 * Critical-endpoint runtime drift detection.
 *
 * Per CLAUDE.md "Frontend (Sparta)" non-negotiable rule §16: for
 * declared critical endpoints (auth, payment, permissions, organization
 * status), the API client runs `safeParse` against the shared Zod
 * response schema and invokes a drift reporter on parse failure. The
 * runtime parse acts as a defence-in-depth check against backend
 * schema drift that the OpenAPI types failed to catch (e.g. nullable
 * fields silently flipped to required).
 *
 * Phase 0 ships the registry empty. Phase 1 features register their
 * critical endpoints (e.g. `/auth/me`, `/auth/login`). The reporter
 * is wired by the consuming portal (apps/admin) to
 * `Sentry.captureException` so drift surfaces in production.
 */
const registry = new Map();
function key(method, path) {
    return `${method} ${path}`;
}
export function registerCriticalEndpoint(endpoint) {
    const k = key(endpoint.method, endpoint.path);
    if (registry.has(k)) {
        throw new Error(`Critical endpoint already registered: ${k}. ` +
            `Each (method, path) pair may have at most one registration.`);
    }
    registry.set(k, endpoint);
}
export function findCriticalEndpoint(method, path) {
    return registry.get(key(method, path));
}
export function listCriticalEndpoints() {
    return Array.from(registry.values());
}
/** @internal — for tests that need a clean registry between cases. */
export function _clearCriticalEndpoints() {
    registry.clear();
}
/**
 * Apply the registered Zod schema (if any) to a response body. On parse
 * failure, invoke the reporter and return `{ valid: false }` so the
 * caller can decide how to surface the error to the user.
 *
 * Note: the response is NOT modified. Callers receive the original body
 * regardless of validation outcome — this hook is observation-only.
 */
export function checkCriticalEndpointResponse(method, path, responseBody, reporter) {
    const endpoint = findCriticalEndpoint(method, path);
    if (!endpoint) {
        return { valid: true };
    }
    const result = endpoint.responseSchema.safeParse(responseBody);
    if (!result.success) {
        reporter({
            endpoint,
            parseError: result.error,
            responseBody,
        });
        return { valid: false, error: result.error };
    }
    return { valid: true };
}
//# sourceMappingURL=critical-endpoints.js.map